import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import type { PaymentCondition } from '../pages/DashboardPage/views/FinancesView/masters/financeMasters';
import {
  getAccessToken,
  getActiveCompanyId,
  buildQuery,
  unwrapError,
  unwrapList,
  unwrapRecord,
  unwrapRecordOrObject,
  getTransportError
} from './apiUtils';

const ROUTES = {
  list:   '/payment_conditions_list',
  create: '/payment_conditions_create',
  update: '/payment_conditions_update',
  remove: '/payment_conditions_delete'
};

export type PaymentConditionInput = Omit<PaymentCondition, 'id' | 'dbId'>;

// El SQL de los .avap se compone con '%s': una comilla simple en el texto lo rompería
const clean = (value: string) => value.replace(/'/g, '’').trim();

const mapPaymentCondition = (raw: any): PaymentCondition => ({
  id:          String(raw.code ?? ''),
  dbId:        raw.id != null ? String(raw.id) : '',
  name:        raw.name || '',
  days:        Number(raw.days) || 0,
  description: raw.description || '',
  isActive:    !(raw.is_active === false || raw.is_active === 'false')
});

// Lo usan 4 pantallas a la vez: una sola petición por sociedad mientras la lista esté fresca
const CACHE_MS = 30000;
let cache: { companyId: string; at: number; promise: Promise<PaymentCondition[]> } | null = null;

export const invalidatePaymentConditions = () => { cache = null; };

export const listPaymentConditions = (options: { force?: boolean } = {}): Promise<PaymentCondition[]> => {
  const token = getAccessToken();
  if (!token) return Promise.reject(new Error('Sesión no válida o expirada.'));
  const companyId = getActiveCompanyId();

  if (!options.force && cache && cache.companyId === companyId && Date.now() - cache.at < CACHE_MS) {
    return cache.promise;
  }

  const promise = ObexRequestsHandlerP80
    .get(`${ROUTES.list}${buildQuery({ access_token: token, company_id: companyId })}`)
    .then((res: any) => {
      const transportError = getTransportError(res);
      if (transportError) throw new Error(transportError);
      return unwrapList(res).map(mapPaymentCondition);
    });

  cache = { companyId, at: Date.now(), promise };
  // Un fallo no se queda en caché
  promise.catch(() => { if (cache && cache.promise === promise) cache = null; });
  return promise;
};

export const createPaymentCondition = async (code: string, data: PaymentConditionInput): Promise<PaymentCondition> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.create}${buildQuery({
    access_token: token,
    company_id:   getActiveCompanyId(),
    code,
    name:         clean(data.name),
    days:         data.days,
    description:  clean(data.description),
    is_active:    data.isActive
  })}`);
  invalidatePaymentConditions();
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al dar de alta la condición de pago.'));
  return mapPaymentCondition(record);
};

export const updatePaymentCondition = async (dbId: string, data: PaymentConditionInput): Promise<PaymentCondition> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.update}${buildQuery({
    access_token: token,
    id:           dbId,
    name:         clean(data.name),
    days:         data.days,
    description:  clean(data.description),
    is_active:    data.isActive
  })}`);
  invalidatePaymentConditions();
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al modificar la condición de pago.'));
  return mapPaymentCondition(record);
};

export const deletePaymentCondition = async (dbId: string): Promise<void> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.remove}${buildQuery({ access_token: token, id: dbId })}`);
  invalidatePaymentConditions();
  // El endpoint responde {message}; un error llega como string plano
  const out = unwrapRecordOrObject(res);
  if (!out || !out.message) throw new Error(unwrapError(res, 'Error al eliminar la condición de pago.'));
};
